/**
 * uploadProgress — pure helpers for the upload chip's "reading" progress (ED-2496).
 *
 * The backend publishes per-document ingestion stages on the `ingest_*` Ably
 * channel (`Ingestion Progress`, stages `extracted` → `findings`; see
 * backend/api/client/controllers/document/process/index.js). UploadJobContext
 * feeds those stages through these helpers so the chip shows a moving bar while
 * Ellie reads a file, instead of a flat "0 of 1 read · 0%" spinner.
 *
 * Kept pure (no React/Ably) so the math is unit-tested in isolation.
 */

export type IngestStage = 'extracted' | 'findings';

/** Stage order, earliest → latest. */
export const STAGE_ORDER: readonly IngestStage[] = ['extracted', 'findings'] as const;

/**
 * Fraction of a single pending document considered "read" once it has reached a
 * given stage. A doc that's been issued but hasn't reported any stage yet still
 * gets a small sliver so the bar never sits at a frozen 0%.
 */
export const INGEST_BASE_CREDIT = 0.08;
export const STAGE_CREDIT: Record<IngestStage, number> = {
  extracted: 0.5, // text/audio pulled — the bulk of the wait (OCR) is what's left
  findings: 0.85, // meds/contacts/events extracted — completion is imminent
};

/** Per-document completion credit for its furthest-reached stage. */
export function docCredit(stage: IngestStage | undefined | null): number {
  return stage ? STAGE_CREDIT[stage] : INGEST_BASE_CREDIT;
}

/**
 * Overall "reading" fraction in [0, 0.97] while ingesting.
 *  - `done` = documents already fully processed (or failed) for this job.
 *  - `pendingStages` = furthest stage of each doc still in flight (undefined =
 *    no stage reported yet).
 * Capped below 1 so the bar only completes when the job is genuinely done.
 */
export function computeIngestFraction(
  total: number,
  done: number,
  pendingStages: ReadonlyArray<IngestStage | undefined>,
): number {
  if (!total || total <= 0) return 0;
  const inflight = pendingStages.reduce((sum, s) => sum + docCredit(s), 0);
  const frac = (done + inflight) / total;
  return Math.max(0, Math.min(0.97, frac));
}

/**
 * The LEAST-advanced stage across the pending docs — i.e. how far the slowest
 * file has gotten. Drives the chip's stage label and the findings fast-complete.
 * Returns null if there are no pending docs, or if any pending doc hasn't
 * reported a stage yet (we're not confident enough to label progress).
 */
export function slowestStage(
  pendingStages: ReadonlyArray<IngestStage | undefined>,
): IngestStage | null {
  if (pendingStages.length === 0) return null;
  let min = STAGE_ORDER.length; // sentinel beyond the last index
  for (const s of pendingStages) {
    const i = s ? STAGE_ORDER.indexOf(s) : -1;
    if (i < min) min = i;
  }
  return min >= 0 ? STAGE_ORDER[min] : null;
}

/**
 * True once there's at least one pending doc and EVERY pending doc has reached
 * `findings` — the expensive OCR/extraction is finished for all of them, so the
 * documents-channel completion is imminent. UploadJobContext uses this to arm a
 * short grace timer that resolves the chip even if that completion is missed.
 */
export function allAtFindings(
  pendingStages: ReadonlyArray<IngestStage | undefined>,
): boolean {
  return pendingStages.length > 0 && pendingStages.every((s) => s === 'findings');
}

/** Friendly subtitle for the single-file "reading" state, by slowest stage. */
export function stagePhrase(stage: IngestStage | null): string {
  switch (stage) {
    case 'findings':
      return 'Almost done…';
    case 'extracted':
      return 'Reading the contents…';
    default:
      return 'Getting started…';
  }
}
